import React, { useEffect, useState } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import AdminSupport from './AdminSupport';
import AdminReports from './AdminReports'; // 👈 Import Reports
import './AdminPanel.css';

export default function AdminPanel() {
    const [user, loading] = useAuthState(auth);
    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'support'

    // User Management State
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate('/login');
            return;
        }
        if (activeTab === 'users') {
            fetchUsers();
        }
    }, [user, loading, navigate, activeTab]);

    const fetchUsers = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'users'));
            const userList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by createdAt descending (Newest first)
            userList.sort((a, b) => {
                const getTime = (dateObj) => {
                    if (!dateObj) return 0;
                    if (dateObj.seconds) return dateObj.seconds; // Firestore Timestamp
                    if (dateObj instanceof Date) return dateObj.getTime() / 1000; // JS Date
                    if (typeof dateObj.toDate === 'function') return dateObj.toDate().getTime() / 1000; // Timestamp method
                    return 0;
                };

                const aTime = getTime(a.createdAt);
                const bTime = getTime(b.createdAt);
                return bTime - aTime;
            });

            setUsers(userList);
        } catch (error) {
            console.error("Error fetching users:", error);
            // alert("Failed to fetch users. Ensure you have permissions.");
        }
    };

    const formatDate = (dateObj) => {
        if (!dateObj) return "N/A";
        try {
            let date;
            if (dateObj.toDate && typeof dateObj.toDate === 'function') {
                date = dateObj.toDate();
            } else if (dateObj.seconds) {
                date = new Date(dateObj.seconds * 1000);
            } else if (dateObj instanceof Date) {
                date = dateObj;
            } else {
                return "Invalid Date";
            }
            return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return "Error";
        }
    };

    const formatLastActive = (devices) => {
        if (!devices || !Array.isArray(devices) || devices.length === 0) return "Offline";
        // Find most recent active time
        const lastActive = Math.max(...devices.map(d => d.lastActive || 0));
        if (lastActive === 0) return "Offline";

        return new Date(lastActive).toLocaleDateString() + " " + new Date(lastActive).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // User Management Handlers
    const handleSearch = (e) => setSearchTerm(e.target.value);

    const filteredUsers = users.filter(u =>
        (u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        (u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || '')
    );

    const handleDelete = async (userId) => {
        if (window.confirm("Are you sure you want to delete this user? This removes their data from Firestore.")) {
            try {
                await deleteDoc(doc(db, "users", userId));
                setUsers(users.filter(u => u.id !== userId));
            } catch (err) {
                console.error("Error deleting user:", err);
                alert("Error deleting user: " + err.message);
            }
        }
    };

    const handleBlock = async (userId, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            await updateDoc(doc(db, "users", userId), {
                isBlocked: newStatus
            });
            setUsers(users.map(u => u.id === userId ? { ...u, isBlocked: newStatus } : u));
        } catch (err) {
            console.error("Error updating block status:", err);
            alert("Error: " + err.message);
        }
    };

    const openModal = (userToEdit = null) => {
        setCurrentUser(userToEdit ? { ...userToEdit } : { name: '', email: '', role: 'user', isBlocked: false });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentUser(null);
    };

    const handleSave = async () => {
        if (!currentUser.email) return alert("Email is required");

        try {
            if (currentUser.id) {
                // Edit existing
                const userRef = doc(db, "users", currentUser.id);
                const { id, ...dataToUpdate } = currentUser; // Exclude ID from data
                await updateDoc(userRef, dataToUpdate);

                setUsers(users.map(u => u.id === currentUser.id ? currentUser : u));
            } else {
                // Create new
                alert("Note: This only creates a User Profile.");
                const newRef = doc(collection(db, "users"));
                await setDoc(newRef, { ...currentUser, createdAt: new Date() });
                setUsers([...users, { ...currentUser, id: newRef.id }]);
            }
            closeModal();
        } catch (err) {
            console.error("Error saving user:", err);
            alert("Error saving: " + err.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentUser(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <div className="admin-container relative flex h-screen bg-gray-100 dark:bg-slate-900">
            {/* Mobile Hamburger */}
            <button
                className="md:hidden absolute top-4 left-4 z-50 p-2 bg-indigo-600 text-white rounded-md shadow-lg"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
                {isMobileMenuOpen ? '✕' : '☰'}
            </button>

            {/* Sidebar Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 md:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>
            )}

            <div className={`admin-sidebar fixed md:relative z-40 h-full w-64 bg-slate-800 text-white transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} flex flex-col`}>
                <h2 className="text-2xl font-bold p-6 border-b border-slate-700">ChatIQ Admin</h2>
                <div
                    className={`admin-menu-item p-4 hover:bg-slate-700 cursor-pointer ${activeTab === 'users' ? 'active bg-slate-700 border-l-4 border-indigo-500' : ''}`}
                    onClick={() => { setActiveTab('users'); setIsMobileMenuOpen(false); }}
                >
                    User Management
                </div>
                <div
                    className={`admin-menu-item p-4 hover:bg-slate-700 cursor-pointer ${activeTab === 'support' ? 'active bg-slate-700 border-l-4 border-indigo-500' : ''}`}
                    onClick={() => { setActiveTab('support'); setIsMobileMenuOpen(false); }}
                >
                    Support Tickets
                </div>
                <div
                    className={`admin-menu-item p-4 hover:bg-slate-700 cursor-pointer ${activeTab === 'reports' ? 'active bg-slate-700 border-l-4 border-indigo-500' : ''}`}
                    onClick={() => { setActiveTab('reports'); setIsMobileMenuOpen(false); }}
                >
                    User Reports
                </div>

                <div className="mt-auto border-t border-slate-700 p-4">
                    <div className="admin-menu-item p-2 hover:text-indigo-400 cursor-pointer mb-2" onClick={() => navigate('/')}>Back to Site</div>
                    <div className="admin-menu-item p-2 text-red-400 hover:text-red-300 cursor-pointer" onClick={() => auth.signOut()}>Logout</div>
                </div>
            </div>

            <div className="admin-content">
                {activeTab === 'users' ? (
                    <>
                        <div className="admin-header">
                            <h1>Users</h1>
                            <button className="admin-btn btn-primary" onClick={() => openModal()}>+ Add User</button>
                        </div>

                        <div className="user-table-container bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                            <div style={{ padding: '15px' }} className="border-b border-gray-100 dark:border-gray-700">
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="w-full md:w-80 p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-slate-700 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="user-table w-full text-left border-collapse">
                                    <thead>
                                        <tr>
                                            <th>User Info</th>
                                            <th>Role</th>
                                            <th>Created / Active</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map(u => (
                                            <tr key={u.id}>
                                                <td>
                                                    <div style={{ fontWeight: 'bold' }} className="flex items-center gap-2">
                                                        {u.name || 'Anonymous User'}
                                                        {u.emailVerified ? (
                                                            <span title="Verified Email" className="bg-green-100 text-green-600 text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-green-200">✓ Verified</span>
                                                        ) : (
                                                            <span title="Unverified Email" className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-gray-200">Unverified</span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{u.email}</div>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                                                        {u.role || 'User'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.85rem' }}>Created: {formatDate(u.createdAt)}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                                        Last Seen: {formatLastActive(u.devices)}
                                                    </div>
                                                </td>
                                                <td>
                                                    {u.isBlocked ?
                                                        <span className="status-badge status-blocked">Blocked</span> :
                                                        <span className="status-badge status-active">Active</span>
                                                    }
                                                </td>
                                                <td>
                                                    <div className="action-grp">
                                                        <button className="admin-btn btn-secondary" onClick={() => openModal(u)}>Edit</button>
                                                        <button
                                                            className={`admin-btn ${u.isBlocked ? 'btn-success' : 'btn-danger'}`}
                                                            onClick={() => handleBlock(u.id, u.isBlocked)}
                                                        >
                                                            {u.isBlocked ? 'Unblock' : 'Block'}
                                                        </button>
                                                        <button className="admin-btn btn-danger" onClick={() => handleDelete(u.id)}>Delete</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {isModalOpen && currentUser && (
                            <div className="modal-overlay">
                                <div className="modal-content">
                                    <h2>{currentUser.id ? 'Edit User' : 'Add User'}</h2>

                                    <div className="form-group">
                                        <label>Name</label>
                                        <input name="name" value={currentUser.name || ''} onChange={handleInputChange} />
                                    </div>

                                    <div className="form-group">
                                        <label>Email</label>
                                        <input name="email" value={currentUser.email || ''} onChange={handleInputChange} />
                                    </div>

                                    <div className="form-group">
                                        <label>Role</label>
                                        <select name="role" value={currentUser.role || 'user'} onChange={handleInputChange}>
                                            <option value="user">User</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>

                                    <div className="modal-actions">
                                        <button className="admin-btn btn-secondary" onClick={closeModal}>Cancel</button>
                                        <button className="admin-btn btn-primary" onClick={handleSave}>Save</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : activeTab === 'reports' ? (
                    <AdminReports openModal={openModal} />
                ) : (
                    <AdminSupport />
                )}
            </div>
        </div>
    );
}
